/**
 * FFS-MNT plan picker. The four BCNC groups are in-network plans sharing slug `BCNC`; Delaware
 * First Health Medicaid is a separate slug (`DFH-MCAID`). All insurance rows are told apart by
 * `groupId`.
 *
 * `groupId` is the React key, the `<option value>`, and the value persisted as the `PLAN-GROUP`
 * Observation component. `payerId` is what the server receives. Labels are member-facing copy.
 *
 * One slug rather than four because the active-member guard matches on the `payer-id` identifier: a
 * member who enrolled under one group and returned selecting another would not match their own
 * record and could enroll twice.
 *
 * Aetna and UnitedHealthcare were removed (out of network). Their server-side registry entries
 * remain, so re-enabling either is a one-line change here.
 */
export const SELF_PAY_PAYER_ID = 'SELF-PAY';

/**
 * The `groupId` of the self-pay option. Exported because two callers need to SELECT self-pay rather
 * than merely recognise it: the option list below, and the page's "Continue without insurance"
 * handler, which rebuilds the member's input as self-pay after a coverage check could not be
 * completed. One literal, one owner — a rename here cannot leave the handler submitting a group id
 * the server does not know.
 */
export const SELF_PAY_GROUP_ID = 'self-pay';

export const SUPPORTED_PAYERS: readonly {
  groupId: string;
  payerId: string;
  label: string;
}[] = [
  { groupId: 'ppo-nc', payerId: 'BCNC', label: 'BCBS NC- PPO-NC-OPTIONS' },
  { groupId: 'blue-value-nc', payerId: 'BCNC', label: 'BCBS NC- Blue Value- NC VALUE' },
  { groupId: 'hmo-blue-care', payerId: 'BCNC', label: 'BCBS NC- HMO- Blue Care' },
  {
    groupId: 'comprehensive-major-medical',
    payerId: 'BCNC',
    label: 'BCBS NC- Comprehensive Major Medical Network',
  },
  {
    groupId: 'delaware-first-health',
    payerId: 'DFH-MCAID',
    label: 'Delaware First Health',
  },
  { groupId: SELF_PAY_GROUP_ID, payerId: SELF_PAY_PAYER_ID, label: 'I would like to self-pay' },
];

/** Payer slug for a group id, or undefined when the group is unknown. */
export function payerIdForGroup(groupId: string): string | undefined {
  return SUPPORTED_PAYERS.find((p) => p.groupId === groupId)?.payerId;
}
